import React, { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import CastingForm from "./CastingForm";
import { CREATE_CASTING } from "../../actions/ActionTypes";
import { putApi } from "../../actions/SendActions";
import MediaAPI from "../../api/MediaAPI";
import "../../css/admin/casting.scss";
import { getDefaultProjectLanguage } from "../../utils/data/Content";
import slugify from "../../utils/slug";
import { slugIsValid, transformSlug } from "../../utils/Translations";

import type { JSX } from "react";

import type { CastingTranslationsHandle } from "./CastingTranslations";
import type { GetCastingResponse } from "../../api/MediaAPI.types";
import type { AppState } from "../../reducers";

export default function CastingPage(): JSX.Element {
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const dispatch = useDispatch();

  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const history = useHistory();
  const [casting, setCasting] = useState<GetCastingResponse>();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation("editor-casting");

  const translationsRef = useRef<CastingTranslationsHandle>(null);
  const queryClient = useQueryClient();

  const isNewCasting = id === "new";

  const getCastingQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getCasting,
    params: [id],
    options: {
      enabled: Boolean(id) && !isNewCasting,
    },
  });

  const getCastingListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getCastingList,
    params: [],
  });

  const { data: castingData, isLoading } = useQuery(getCastingQuery);

  const updateCastingMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: GetCastingResponse;
    }) => MediaAPI.updateCasting({ uuid, data }),
    onSuccess: (updatedCasting) => {
      toast.success(t("editor-notif:saveSuccess"));
      setCasting(updatedCasting);
      queryClient.setQueryData(getCastingQuery.queryKey, updatedCasting);
      queryClient.invalidateQueries(getCastingListQuery.queryKey);
      translationsRef.current?.onSaveSuccess();
    },
    onError: () => {
      toast.error(t("editor-notif:saveError"));
    },
  });

  const createCasting = useCallback(() => {
    const {
      language,
      language_uuid: languageUuid,
      language_uuid_index: languageUuidIndex,
    } = getDefaultProjectLanguage(languages, project);

    dispatch(
      putApi({
        endpoint: "/api/media/v3/admin/castings/",
        type: CREATE_CASTING,
        item: "castings",
        token,
        data: {
          label: t("newCasting"),
          translations: [
            {
              default: true,
              language,
              language_uuid: languageUuid,
              biography: "",
              signature: "",
              name: "",
            },
          ],
          translations_indexes: {
            language_uuid: languageUuidIndex,
            default: { true: [0] },
          },
        },
        message: t("editor-notif:saveSuccess"),
        error: t("editor-notif:saveError"),
        callback: ({ uuid }: { uuid: string }) => {
          queryClient.invalidateQueries(getCastingListQuery.queryKey);
          history.replace(`/castings/${uuid}`);
        },
      }),
    );
  }, [
    dispatch,
    token,
    t,
    languages,
    project,
    history,
    queryClient,
    getCastingListQuery.queryKey,
  ]);

  const hasCreatedRef = useRef(false);

  useEffect(() => {
    if (isNewCasting && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      createCasting();
    }
  }, [isNewCasting, createCasting]);

  useEffect(() => {
    if (!isNewCasting) {
      setCasting(castingData);
    }
  }, [castingData, isNewCasting]);

  const onUpdate = async (): Promise<void> => {
    if (!casting) return;

    const isValidForm = (await translationsRef.current?.isValid()) ?? true;
    if (!isValidForm) {
      toast.error(t("form:validationError"));
      return;
    }

    const { translations, translations_indexes: translationsIndexes } =
      translationsRef.current?.getApiTranslations?.() ?? {
        translations: [],
        translations_indexes: { default: { true: [0] }, language_uuid: {} },
      };

    const updatedCastingData = {
      ...casting,
      translations: translations as GetCastingResponse["translations"],
      translationsIndexes,
    };

    if (!updatedCastingData.slug) {
      const defaultTranslation = translations.find(
        (translation) => translation.default,
      );
      if (defaultTranslation?.name) {
        updatedCastingData.slug = slugify(defaultTranslation.name);
      }
    }

    if (!slugIsValid(updatedCastingData.slug)) {
      updatedCastingData.slug = transformSlug(
        updatedCastingData.slug,
      ) as string;
    }

    try {
      await updateCastingMutation.mutateAsync({
        uuid: updatedCastingData.uuid,
        data: updatedCastingData,
      });
    } catch (error) {
      console.error("Error updating casting:", error);
    }
  };

  return (
    <div className="editor-update-container">
      {casting ? (
        <CastingForm
          casting={casting}
          isLoading={isLoading}
          onSubmit={onUpdate}
          translationsRef={translationsRef}
          type="edit"
          setCasting={
            setCasting as React.Dispatch<
              React.SetStateAction<GetCastingResponse>
            >
          }
        />
      ) : null}
    </div>
  );
}
